/**
 * Per-route document head management for the SPA.
 *
 * The app ships a single static `index.html`, so before this every route served
 * the *same* title, description and — most damagingly — the same
 * `<link rel="canonical" href="https://bunnycal.io/">`. A self-referencing
 * canonical pointing at `/` told Google that `/pricing`, `/about`, `/privacy`
 * and every other route were duplicates of the homepage, which keeps them out
 * of the index entirely.
 *
 * There is no SSR here, so crawlers that don't execute JavaScript still see the
 * static shell. Googlebot does render, so these tags are read; the static
 * fallback in index.html stays homepage-accurate for everything else. Genuine
 * fix for the non-rendering crawlers is prerendering — see the SEO report.
 *
 * Tags are *reconciled* rather than blindly appended: each managed tag carries
 * `data-seo`, so navigating between routes updates the existing element instead
 * of stacking duplicates, and anything left over from a previous route is
 * removed on unmount.
 */

export const SITE_ORIGIN = "https://bunnycal.io";
export const SITE_NAME = "BunnyCal";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image-v2.jpg`;

/** Marks every element this module owns, so cleanup never touches hand-written tags. */
const MANAGED_ATTR = "data-seo";

export interface SeoInput {
  /** Full <title>. Include the brand — this is used verbatim. */
  title: string;
  description: string;
  /** Root-relative path, e.g. "/pricing". Becomes the canonical and og:url. */
  path: string;
  /** Absolute URL. Defaults to the shared social card. */
  image?: string;
  /** "website" (default) or "article". */
  ogType?: string;
  /** Keeps the page out of search results — auth and other private routes. */
  noindex?: boolean;
  /** JSON-LD objects rendered into <script type="application/ld+json">. */
  jsonLd?: Array<Record<string, unknown>>;
}

/** Absolute, trailing-slash-normalised URL for a root-relative path. */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Upsert a <meta> tag by its identifying attribute.
 *
 * `key` is "name" for standard/Twitter meta and "property" for Open Graph —
 * Facebook's parser only reads `property`, so the two can't be collapsed.
 */
function setMeta(key: "name" | "property", value: string, content: string) {
  const selector = `meta[${key}="${CSS.escape(value)}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute(MANAGED_ATTR, "");
}

function setLink(rel: string, href: string) {
  const selector = `link[rel="${CSS.escape(rel)}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.setAttribute(MANAGED_ATTR, "");
}

/**
 * Replace all managed JSON-LD blocks.
 *
 * Structured data is rewritten wholesale on every route change rather than
 * diffed: a stale Product block left on an article page is a schema error, and
 * these payloads are far too small for the churn to matter.
 */
function setJsonLd(blocks: Array<Record<string, unknown>>) {
  document.head
    .querySelectorAll(
      `script[type="application/ld+json"][${MANAGED_ATTR}], script[type="application/ld+json"][data-home-schema]`
    )
    .forEach((node) => node.remove());

  for (const block of blocks) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(MANAGED_ATTR, "");
    // Not user input — these are static objects defined in the bundle. Escaping
    // "<" anyway keeps a future dynamic value from being able to close the tag.
    script.textContent = JSON.stringify(block).replace(/</g, "\\u003c");
    document.head.appendChild(script);
  }
}

/**
 * Apply a page's SEO tags and return a cleanup function.
 *
 * Framework-agnostic on purpose so it can be called from a `useEffect`, tested
 * directly, or reused if the app ever moves to a different router.
 */
export function applySeo(input: SeoInput): () => void {
  const url = absoluteUrl(input.path);
  const image = input.image ?? DEFAULT_OG_IMAGE;
  const ogType = input.ogType ?? "website";

  document.title = input.title;

  setMeta("name", "description", input.description);
  setLink("canonical", url);

  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:title", input.title);
  setMeta("property", "og:description", input.description);
  setMeta("property", "og:url", url);
  setMeta("property", "og:type", ogType);
  setMeta("property", "og:image", image);
  setMeta("property", "og:image:alt", input.title);

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", input.title);
  setMeta("name", "twitter:description", input.description);
  setMeta("name", "twitter:image", image);
  setMeta("name", "twitter:image:alt", input.title);

  // `robots` is only emitted when the page opts out. Leaving the tag off
  // entirely is equivalent to "index, follow" and avoids a stale noindex
  // surviving on a route that should be indexed.
  const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (input.noindex) {
    setMeta("name", "robots", "noindex, nofollow");
  } else if (robots?.hasAttribute(MANAGED_ATTR)) {
    robots.remove();
  }

  setJsonLd(input.jsonLd ?? []);

  return () => {
    document.head
      .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}]`)
      .forEach((node) => node.remove());
    document.head.querySelector('meta[name="robots"][data-seo]')?.remove();
  };
}
